import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import moment from 'moment'
import { useIntl } from 'gatsby-plugin-intl'
import { CircularProgress } from '@material-ui/core'
import AddOutlinedIcon from '@material-ui/icons/AddOutlined'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import EditOutlinedIcon from '@material-ui/icons/EditOutlined'
import GroupOutlinedIcon from '@material-ui/icons/GroupOutlined'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import PersonOutlineOutlinedIcon from '@material-ui/icons/PersonOutlineOutlined'
import SettingsOutlinedIcon from '@material-ui/icons/SettingsOutlined'
import StarBorderIcon from '@material-ui/icons/StarBorder'
import StarIcon from '@material-ui/icons/Star'
import { useDispatch, useSelector } from 'react-redux'
import { API_STATES, BOOLEAN, DATE_FORMAT } from 'src/ducks/consts'
import { breakpoint } from 'src/theme/grid'
import Alert from 'src/components/Alert'
import EllipsisTooltip from 'src/components/EllipsisTooltip'
import TextInput from 'src/components/atoms/TextInput'
import Checkbox from 'src/components/atoms/CheckboxInput'
import PermissionsContext, {
  hasPermissionTo,
  PERMISSIONS
} from 'src/utils/permissions'
import {
  BaseDialog,
  Dialog,
  DialogText,
  ActionBar,
  ConfirmButton,
  CancelButton,
  ModalCloseIcon
} from 'src/components/Dialog'
import { setSnackbarValuesRoutine } from 'src/ducks/actions'
import { setDefaultOfferVisibilityRoutine } from 'src/features/account/duck/actions'
import {
  useAddRoomMember,
  useCandidatesSearch,
  useCreateRoom,
  useDeleteRoom,
  useRoomMembers,
  useRoomsList,
  useRemoveRoomMember,
  useUpdateRoom
} from './hooks/useExchangeRooms'

const MIN_ROOM_NAME_LENGTH = 3
const MIN_SEARCH_LENGTH = 3

const getExchangeLabelId = exchangeType => {
  switch (exchangeType) {
    case 'export-cargo':
      return 'exchangeRooms.exchange.export'
    case 'import-cargo':
      return 'exchangeRooms.exchange.import'
    case 'post-import-containers':
      return 'exchangeRooms.exchange.postImport'
    case 'free-carriers':
      return 'exchangeRooms.exchange.freeCarriers'
    case 'general-cargo':
      return 'exchangeRooms.exchange.general'
    default:
      return 'exchangeRooms.exchange.generic'
  }
}

const getCompanyId = entity =>
  entity?.company?.id || entity?.companyId || entity?.company_id || null

const getRoomCreatedAtLabel = room => {
  if (!room?.createdAt) {
    return null
  }

  return moment(room.createdAt).format(DATE_FORMAT)
}

const getRoomMemberAddedAtLabel = (member, intl) => {
  const addedAt =
    member?.joinedAt ||
    member?.joined_at ||
    member?.createdAt ||
    member?.created_at ||
    null

  if (!addedAt) {
    return null
  }

  return intl.formatMessage(
    {
      id: 'exchangeRooms.memberAddedAt',
      defaultMessage: 'Added: {date}'
    },
    { date: moment(addedAt).format(DATE_FORMAT) }
  )
}

const getCompanyDetailsLabel = (company, intl) => {
  const parts = []

  if (company?.address) {
    parts.push(company.address)
  }

  if (company?.taxId) {
    parts.push(`${intl.formatMessage({ id: 'common.nip' })}: ${company.taxId}`)
  }

  return parts.join(' • ') || null
}

const getDefaultRoomActionLabel = (intl, isDefault) =>
  intl.formatMessage({
    id: isDefault
      ? 'exchangeRooms.removeDefaultRoomAction'
      : 'exchangeRooms.defaultRoomAction'
  })

const getDefaultRoomCheckboxLabel = intl =>
  intl.formatMessage({ id: 'exchangeRooms.defaultRoomAction' })

const getDefaultRoomFeedbackMessageId = (isDefault, type = 'success') => {
  if (type === 'error') {
    return isDefault
      ? 'exchangeRooms.defaultSetError'
      : 'exchangeRooms.defaultUnsetError'
  }

  return isDefault
    ? 'exchangeRooms.defaultSetSuccess'
    : 'exchangeRooms.defaultUnsetSuccess'
}

const flattenInfinitePages = pages =>
  (pages || []).flatMap(page => page?.items || [])

const getInfiniteTotalCount = (pages, fallback = 0) => {
  const total = pages?.[0]?.meta?.total

  return Number.isFinite(Number(total)) ? Number(total) : fallback
}

const getApiErrorMessage = error => {
  const message = error?.data?.message || error?.response?.data?.message || ''

  return typeof message === 'string' ? message : ''
}

const useInfiniteScrollSentinel = ({
  enabled,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  scrollContainerRef = null
}) => {
  const sentinelRef = useRef(null)

  useEffect(() => {
    const node = sentinelRef.current

    if (!enabled || !node || !hasNextPage) {
      return undefined
    }

    if (typeof IntersectionObserver === 'undefined') {
      return undefined
    }

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !isFetchingNextPage) {
            fetchNextPage()
          }
        })
      },
      {
        root: scrollContainerRef?.current || null,
        rootMargin: '200px 0px 200px 0px',
        threshold: 0.01
      }
    )

    observer.observe(node)

    return () => {
      observer.unobserve(node)
      observer.disconnect()
    }
  }, [
    enabled,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    scrollContainerRef
  ])

  return sentinelRef
}

const CompanyListRow = ({
  company,
  metaLabel,
  secondaryLabel,
  action,
  compact = false
}) => (
  <CompanyRow compact={compact}>
    <AvatarPlaceholder>
      <PersonOutlineOutlinedIcon fontSize='small' />
    </AvatarPlaceholder>
    <CompanyInfo>
      <CompanyName>{company?.name}</CompanyName>
      {metaLabel && <CompanyMeta>{metaLabel}</CompanyMeta>}
      {secondaryLabel && (
        <CompanyMetaSubtle>{secondaryLabel}</CompanyMetaSubtle>
      )}
    </CompanyInfo>
    {action}
  </CompanyRow>
)

const AddMemberDialog = ({
  open,
  onClose,
  roomName,
  searchValue,
  onSearchChange,
  candidates,
  candidatesQuery,
  onAddMember,
  addRoomMemberMutation,
  intl
}) => {
  const trimmedSearch = searchValue.trim()
  const candidateListContainerRef = useRef(null)
  const candidateLoadMoreRef = useInfiniteScrollSentinel({
    enabled: Boolean(
      open &&
        trimmedSearch.length >= MIN_SEARCH_LENGTH &&
        candidatesQuery.hasNextPage
    ),
    hasNextPage: candidatesQuery.hasNextPage,
    isFetchingNextPage: candidatesQuery.isFetchingNextPage,
    fetchNextPage: candidatesQuery.fetchNextPage,
    scrollContainerRef: candidateListContainerRef
  })

  let resultsContent = null

  if (trimmedSearch.length >= MIN_SEARCH_LENGTH) {
    if (candidatesQuery.isLoading) {
      resultsContent = (
        <AddMemberResultsState>
          <StateWrapper>
            <CircularProgress size={18} />
            <span>{intl.formatMessage({ id: 'common.loading' })}</span>
          </StateWrapper>
        </AddMemberResultsState>
      )
    } else if (candidatesQuery.isError) {
      resultsContent = (
        <AddMemberResultsState>
          <Alert
            type='alert'
            message={intl.formatMessage({ id: 'common.loadingError' })}
          />
        </AddMemberResultsState>
      )
    } else if (candidates.length) {
      resultsContent = (
        <CandidateList ref={candidateListContainerRef}>
          {candidates.map(candidate => {
            const companyId = getCompanyId(candidate)
            const action = (
              <AddMemberInlineButton
                type='button'
                onClick={() => onAddMember(candidate)}
                disabled={addRoomMemberMutation.isLoading || !companyId}
              >
                <AddOutlinedIcon fontSize='small' />
              </AddMemberInlineButton>
            )

            return (
              <CompanyListRow
                key={
                  companyId ||
                  candidate.company?.taxId ||
                  candidate.company?.name
                }
                company={candidate.company}
                metaLabel={
                  candidate.company?.taxId
                    ? `${intl.formatMessage({ id: 'common.nip' })}: ${
                        candidate.company.taxId
                      }`
                    : null
                }
                secondaryLabel={candidate.company?.address || null}
                action={action}
                compact
              />
            )
          })}
          {(candidatesQuery.isFetchingNextPage ||
            candidatesQuery.hasNextPage) && (
            <ListEndRef ref={candidateLoadMoreRef}>
              {candidatesQuery.isFetchingNextPage && (
                <CircularProgress size={14} />
              )}
            </ListEndRef>
          )}
        </CandidateList>
      )
    } else {
      resultsContent = (
        <AddMemberResultsState>
          <InfoText>
            {intl.formatMessage({
              id: 'forms.privateRoomMemberSearchNoSearchResults'
            })}
          </InfoText>
        </AddMemberResultsState>
      )
    }
  }

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth='sm'
      PaperProps={{
        style: {
          width: 'min(560px, calc(100vw - 32px))',
          maxWidth: '560px',
          maxHeight: 'min(480px, calc(100vh - 32px))',
          margin: 16
        }
      }}
      contentProps={{
        style: {
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }
      }}
    >
      <CompactDialogContent>
        <CompactModalCloseIcon onClick={onClose} data-testid='dialog-close' />

        <CompactDialogHeader>
          <CompactDialogTitle>
            {intl.formatMessage({ id: 'exchangeRooms.addMemberDialogTitle' })}
          </CompactDialogTitle>
          <CompactDialogDescription>
            {intl.formatMessage(
              { id: 'exchangeRooms.addMemberDialogDescription' },
              { roomName: roomName || '' }
            )}
          </CompactDialogDescription>
        </CompactDialogHeader>

        <AddMemberSearchField>
          <TextInput
            id='exchange-room-member-search'
            name='candidateSearch'
            value={searchValue}
            onChange={(name, value) => onSearchChange(value)}
            placeholder={intl.formatMessage({
              id: 'exchangeRooms.addMemberSearchPlaceholder'
            })}
            type='search'
          />
        </AddMemberSearchField>

        <AddMemberSearchHint>
          {intl.formatMessage(
            { id: 'forms.min_x_chars' },
            { value: MIN_SEARCH_LENGTH }
          )}
        </AddMemberSearchHint>

        {resultsContent ? (
          <AddMemberResultsArea>{resultsContent}</AddMemberResultsArea>
        ) : (
          <AddMemberResultsPlaceholder>
            <InfoText>
              {intl.formatMessage({
                id: 'exchangeRooms.addMemberSearchPrompt',
                defaultMessage:
                  'Wpisz nazwę firmy, aby zobaczyć listę kandydatów.'
              })}
            </InfoText>
          </AddMemberResultsPlaceholder>
        )}
      </CompactDialogContent>
    </BaseDialog>
  )
}

const RoomEditorDialog = ({
  open,
  onClose,
  title,
  roomName,
  onRoomNameChange,
  roomDefault,
  onRoomDefaultChange,
  onSave,
  isSaving,
  intl
}) => {
  const trimmedRoomName = roomName.trim()
  const roomNameTooShort =
    trimmedRoomName.length > 0 && trimmedRoomName.length < MIN_ROOM_NAME_LENGTH
  const handleRoomDefaultToggle = () => {
    onRoomDefaultChange(current => !current)
  }

  return (
    <Dialog open={open} onClose={onClose} title={title} maxWidth='sm'>
      <TextInput
        label={intl.formatMessage({ id: 'exchangeRooms.roomNameLabel' })}
        name='roomName'
        value={roomName}
        onChange={(name, value) => onRoomNameChange(value)}
        placeholder={intl.formatMessage({
          id: 'exchangeRooms.roomNamePlaceholder'
        })}
      />

      {roomNameTooShort && (
        <HelperText>
          {intl.formatMessage(
            { id: 'forms.min_x_chars' },
            { value: MIN_ROOM_NAME_LENGTH }
          )}
        </HelperText>
      )}

      <Checkbox
        name='roomDefault'
        value={roomDefault}
        onChange={handleRoomDefaultToggle}
        option={{
          value: BOOLEAN.YES,
          text: getDefaultRoomCheckboxLabel(intl)
        }}
      />

      <EditorActionRow>
        <SecondaryButton type='button' onClick={onClose}>
          {intl.formatMessage({ id: 'common.cancel' })}
        </SecondaryButton>
        <SaveButton
          type='button'
          onClick={onSave}
          disabled={trimmedRoomName.length < MIN_ROOM_NAME_LENGTH || isSaving}
        >
          {isSaving ? (
            <CircularProgress size={16} />
          ) : (
            intl.formatMessage({ id: 'common.save' })
          )}
        </SaveButton>
      </EditorActionRow>
    </Dialog>
  )
}

const RoomCreateDialog = ({
  open,
  onClose,
  roomName,
  onRoomNameChange,
  onSave,
  isSaving,
  intl
}) => {
  const trimmedRoomName = roomName.trim()
  const roomNameTooShort =
    trimmedRoomName.length > 0 && trimmedRoomName.length < MIN_ROOM_NAME_LENGTH

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={intl.formatMessage({ id: 'exchangeRooms.createRoomDialogTitle' })}
      maxWidth='sm'
    >
      <DialogText>
        {intl.formatMessage({
          id: 'exchangeRooms.createRoomDialogDescription'
        })}
      </DialogText>
      <TextInput
        label={intl.formatMessage({ id: 'exchangeRooms.roomNameLabel' })}
        name='roomName'
        value={roomName}
        onChange={(name, value) => onRoomNameChange(value)}
        placeholder={intl.formatMessage({
          id: 'exchangeRooms.roomNamePlaceholder'
        })}
      />

      {roomNameTooShort && (
        <HelperText>
          {intl.formatMessage(
            { id: 'forms.min_x_chars' },
            { value: MIN_ROOM_NAME_LENGTH }
          )}
        </HelperText>
      )}

      <EditorActionRow>
        <SecondaryButton type='button' onClick={onClose}>
          {intl.formatMessage({ id: 'common.cancel' })}
        </SecondaryButton>
        <SaveButton
          type='button'
          onClick={onSave}
          disabled={trimmedRoomName.length < MIN_ROOM_NAME_LENGTH || isSaving}
        >
          {isSaving ? (
            <CircularProgress size={16} />
          ) : (
            intl.formatMessage({ id: 'exchangeRooms.createRoomSubmit' })
          )}
        </SaveButton>
      </EditorActionRow>
    </Dialog>
  )
}

const ManagerOptionsDialog = ({
  open,
  onClose,
  isPrivateOfferDefault,
  onTogglePrivateOfferDefault,
  onSave,
  isSaving,
  isDirty,
  intl
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    title={intl.formatMessage({ id: 'exchangeRooms.optionsDialogTitle' })}
    maxWidth='sm'
  >
    <DialogText>
      {intl.formatMessage({ id: 'exchangeRooms.optionsDialogDescription' })}
    </DialogText>

    <OptionsCard>
      <Checkbox
        name='privateOfferDefault'
        value={isPrivateOfferDefault}
        onChange={onTogglePrivateOfferDefault}
        option={{
          value: BOOLEAN.YES,
          text: intl.formatMessage({ id: 'forms.setPrivateOfferDefault' }),
          description: intl.formatMessage({
            id: 'forms.setPrivateOfferDefaultInfo'
          })
        }}
      />
    </OptionsCard>

    <EditorActionRow>
      <SecondaryButton type='button' onClick={onClose}>
        {intl.formatMessage({ id: 'common.cancel' })}
      </SecondaryButton>
      <SaveButton
        type='button'
        onClick={onSave}
        disabled={isSaving || !isDirty}
      >
        {isSaving ? (
          <CircularProgress size={16} />
        ) : (
          intl.formatMessage({ id: 'common.save' })
        )}
      </SaveButton>
    </EditorActionRow>
  </Dialog>
)

const PrivateRoomsManager = ({ open, onClose, exchangeType }) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const permissions = useContext(PermissionsContext)
  const currentUser = useSelector(state => state.account.currentUser)
  const updateUserStatus = useSelector(state => state.account.updateUser)
  const canManageRooms = hasPermissionTo(
    PERMISSIONS.EXCHANGE_ROOMS_MANAGE,
    permissions
  )
  const isSavingOptions = updateUserStatus?.state === API_STATES.IN_PROGRESS

  const roomsListContainerRef = useRef(null)
  const contentPanelRef = useRef(null)
  const membersListContainerRef = useRef(null)

  const [selectedRoomId, setSelectedRoomId] = useState(null)
  const [isCreateRoomDialogOpen, setCreateRoomDialogOpen] = useState(false)
  const [createRoomName, setCreateRoomName] = useState('')
  const [roomSearchValue, setRoomSearchValue] = useState('')
  const [debouncedRoomSearchValue, setDebouncedRoomSearchValue] = useState('')
  const [roomEditTarget, setRoomEditTarget] = useState(null)
  const [roomEditName, setRoomEditName] = useState('')
  const [roomEditDefault, setRoomEditDefault] = useState(false)
  const [roomPendingDelete, setRoomPendingDelete] = useState(null)
  const [memberFilterValue, setMemberFilterValue] = useState('')
  const [isAddMemberDialogOpen, setAddMemberDialogOpen] = useState(false)
  const [isOptionsDialogOpen, setOptionsDialogOpen] = useState(false)
  const [isSubmittingOptions, setSubmittingOptions] = useState(false)
  const [privateOfferDefaultDraft, setPrivateOfferDefaultDraft] = useState(
    Boolean(currentUser?.isPrivateOfferDefault)
  )
  const [candidateSearchValue, setCandidateSearchValue] = useState('')
  const [
    debouncedCandidateSearchValue,
    setDebouncedCandidateSearchValue
  ] = useState('')
  const [dismissedCandidateIds, setDismissedCandidateIds] = useState([])
  const isPrivateOfferDefaultDirty =
    privateOfferDefaultDraft !== Boolean(currentUser?.isPrivateOfferDefault)

  const roomSearchHelper =
    roomSearchValue.trim().length > 1 &&
    roomSearchValue.trim().length < MIN_SEARCH_LENGTH
      ? intl.formatMessage(
          { id: 'forms.min_x_chars' },
          { value: MIN_SEARCH_LENGTH }
        )
      : null
  const roomSearchQuery =
    debouncedRoomSearchValue.length >= MIN_SEARCH_LENGTH
      ? debouncedRoomSearchValue
      : ''

  const roomsQuery = useRoomsList(exchangeType, roomSearchQuery, {
    enabled: open,
    refetchOnWindowFocus: false
  })

  const roomsPages = roomsQuery.data?.pages || []
  const rooms = useMemo(() => flattenInfinitePages(roomsPages), [roomsPages])
  const roomsTotalCount = useMemo(
    () => getInfiniteTotalCount(roomsPages, rooms.length),
    [rooms.length, roomsPages]
  )
  const selectedRoom = useMemo(
    () => rooms.find(room => room.id === selectedRoomId) || null,
    [rooms, selectedRoomId]
  )

  const memberSearchValue = memberFilterValue.trim()
  const memberSearchEnabled =
    memberSearchValue.length === 0 ||
    memberSearchValue.length >= MIN_SEARCH_LENGTH
  const memberSearchQuery =
    memberSearchValue.length >= MIN_SEARCH_LENGTH ? memberSearchValue : ''
  const memberSearchHelper =
    memberSearchValue.length > 0 && memberSearchValue.length < MIN_SEARCH_LENGTH
      ? intl.formatMessage(
          { id: 'forms.min_x_chars' },
          { value: MIN_SEARCH_LENGTH }
        )
      : null

  const membersQuery = useRoomMembers(
    exchangeType,
    selectedRoomId,
    memberSearchQuery,
    {
      enabled: open && Boolean(selectedRoomId) && memberSearchEnabled,
      refetchOnWindowFocus: false
    }
  )

  const membersPages = membersQuery.data?.pages || []
  const members = useMemo(() => flattenInfinitePages(membersPages), [
    membersPages
  ])
  const membersTotalCount = useMemo(
    () => getInfiniteTotalCount(membersPages, members.length),
    [members.length, membersPages]
  )

  const candidatesQuery = useCandidatesSearch(
    exchangeType,
    selectedRoomId,
    debouncedCandidateSearchValue,
    {
      enabled:
        open &&
        canManageRooms &&
        isAddMemberDialogOpen &&
        Boolean(selectedRoomId) &&
        debouncedCandidateSearchValue.length >= MIN_SEARCH_LENGTH,
      refetchOnWindowFocus: false
    }
  )

  const candidatePages = candidatesQuery.data?.pages || []
  const candidates = useMemo(() => flattenInfinitePages(candidatePages), [
    candidatePages
  ])
  const visibleCandidates = useMemo(() => {
    if (!dismissedCandidateIds.length) {
      return candidates
    }

    const dismissed = new Set(dismissedCandidateIds)
    return candidates.filter(
      candidate => !dismissed.has(getCompanyId(candidate))
    )
  }, [candidates, dismissedCandidateIds])

  const showSnackbar = (type, id, values = {}) => {
    dispatch(
      setSnackbarValuesRoutine({
        type,
        title: '',
        message: intl.formatMessage({ id }, values)
      })
    )
  }

  const closeCreateRoomDialog = () => {
    setCreateRoomDialogOpen(false)
    setCreateRoomName('')
  }

  const closeRoomEditDialog = () => {
    setRoomEditTarget(null)
    setRoomEditName('')
    setRoomEditDefault(false)
  }

  const closeAddMemberDialog = () => {
    setAddMemberDialogOpen(false)
    setCandidateSearchValue('')
    setDebouncedCandidateSearchValue('')
    setDismissedCandidateIds([])
  }

  const openOptionsDialog = () => {
    setPrivateOfferDefaultDraft(Boolean(currentUser?.isPrivateOfferDefault))
    setOptionsDialogOpen(true)
  }

  const closeOptionsDialog = () => {
    setOptionsDialogOpen(false)
    setSubmittingOptions(false)
    setPrivateOfferDefaultDraft(Boolean(currentUser?.isPrivateOfferDefault))
  }

  const createRoomMutation = useCreateRoom(exchangeType, {
    onSuccess: response => {
      const createdRoom = response?.data || null

      if (createdRoom?.id) {
        setSelectedRoomId(createdRoom.id)
      }

      setRoomSearchValue('')
      setDebouncedRoomSearchValue('')
      closeCreateRoomDialog()
      showSnackbar('success', 'exchangeRooms.createSuccess')
    },
    onError: () => {
      showSnackbar('alert', 'exchangeRooms.createError')
    }
  })

  const updateRoomMutation = useUpdateRoom(exchangeType, {
    onSuccess: (response, variables) => {
      const isRoomEditMutation = Boolean(
        roomEditTarget && variables.roomId === roomEditTarget.id
      )
      const isDefaultUpdate = Object.prototype.hasOwnProperty.call(
        variables.payload,
        'isDefault'
      )

      if (isRoomEditMutation) {
        closeRoomEditDialog()
      }

      showSnackbar(
        'success',
        isRoomEditMutation || !isDefaultUpdate
          ? 'exchangeRooms.updateSuccess'
          : getDefaultRoomFeedbackMessageId(
              Boolean(variables.payload.isDefault),
              'success'
            )
      )
    },
    onError: (error, variables) => {
      const isRoomEditMutation = Boolean(
        roomEditTarget && variables.roomId === roomEditTarget.id
      )
      const isDefaultUpdate = Object.prototype.hasOwnProperty.call(
        variables.payload,
        'isDefault'
      )

      showSnackbar(
        'alert',
        isRoomEditMutation || !isDefaultUpdate
          ? 'exchangeRooms.updateError'
          : getDefaultRoomFeedbackMessageId(
              Boolean(variables.payload.isDefault),
              'error'
            )
      )
    }
  })

  const deleteRoomMutation = useDeleteRoom(exchangeType, {
    onSuccess: () => {
      setRoomPendingDelete(null)
      showSnackbar('success', 'exchangeRooms.deleteSuccess')
    },
    onError: error => {
      dispatch(
        setSnackbarValuesRoutine({
          type: 'alert',
          title: '',
          message:
            getApiErrorMessage(error) ||
            intl.formatMessage({ id: 'exchangeRooms.deleteError' })
        })
      )
    }
  })

  const addRoomMemberMutation = useAddRoomMember(exchangeType, {
    onSuccess: async (response, variables) => {
      setDismissedCandidateIds(prev =>
        prev.includes(variables.companyId)
          ? prev
          : [...prev, variables.companyId]
      )
      await Promise.all([roomsQuery.refetch(), membersQuery.refetch()])
      showSnackbar('success', 'exchangeRooms.addMemberSuccess')
    },
    onError: () => {
      showSnackbar('alert', 'exchangeRooms.addMemberError')
    }
  })

  const removeRoomMemberMutation = useRemoveRoomMember(exchangeType, {
    onSuccess: async () => {
      await Promise.all([roomsQuery.refetch(), membersQuery.refetch()])
      showSnackbar('success', 'exchangeRooms.removeMemberSuccess')
    },
    onError: () => {
      showSnackbar('alert', 'exchangeRooms.removeMemberError')
    }
  })

  useEffect(() => {
    if (!open) {
      closeCreateRoomDialog()
      closeRoomEditDialog()
      closeAddMemberDialog()
      closeOptionsDialog()
      setRoomPendingDelete(null)
      setMemberFilterValue('')
      setRoomSearchValue('')
      setDebouncedRoomSearchValue('')
      setDismissedCandidateIds([])
    }
  }, [open])

  useEffect(() => {
    if (!open) {
      return
    }

    if (!rooms.length) {
      setSelectedRoomId(null)
      return
    }

    const hasSelectedRoom = rooms.some(room => room.id === selectedRoomId)

    if (!selectedRoomId || !hasSelectedRoom) {
      setSelectedRoomId(rooms[0].id)
    }
  }, [open, rooms, selectedRoomId])

  useEffect(() => {
    setMemberFilterValue('')
    closeAddMemberDialog()
    setDismissedCandidateIds([])
  }, [selectedRoomId])

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedRoomSearchValue(roomSearchValue.trim())
    }, 300)

    return () => clearTimeout(timeout)
  }, [roomSearchValue])

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedCandidateSearchValue(candidateSearchValue.trim())
    }, 300)

    return () => clearTimeout(timeout)
  }, [candidateSearchValue])

  const openCreateEditor = () => {
    closeRoomEditDialog()
    setCreateRoomName('')
    setCreateRoomDialogOpen(true)
  }

  const openEditEditor = room => {
    closeCreateRoomDialog()
    setRoomEditTarget(room)
    setRoomEditName(room.name || '')
    setRoomEditDefault(Boolean(room.isDefault))
  }

  const handleSaveCreateRoom = () => {
    const trimmedRoomName = createRoomName.trim()

    if (trimmedRoomName.length < MIN_ROOM_NAME_LENGTH) {
      return
    }

    createRoomMutation.mutate({
      name: trimmedRoomName
    })
  }

  const handleSaveRoomEdit = () => {
    const trimmedRoomName = roomEditName.trim()

    if (trimmedRoomName.length < MIN_ROOM_NAME_LENGTH || !roomEditTarget?.id) {
      return
    }

    updateRoomMutation.mutate({
      roomId: roomEditTarget.id,
      payload: {
        name: trimmedRoomName,
        isDefault: roomEditDefault
      }
    })
  }

  const handleToggleDefaultRoom = (roomId, currentDefault) => {
    updateRoomMutation.mutate({
      roomId,
      payload: {
        isDefault: !currentDefault
      }
    })
  }

  const handleAddMember = candidate => {
    const companyId = getCompanyId(candidate)

    if (!companyId || !selectedRoomId) {
      return
    }

    addRoomMemberMutation.mutate({
      roomId: selectedRoomId,
      companyId
    })
  }

  const handleRemoveMember = member => {
    const companyId = getCompanyId(member)

    if (!companyId || !selectedRoomId) {
      return
    }

    removeRoomMemberMutation.mutate({
      roomId: selectedRoomId,
      memberId: companyId
    })
  }

  const handleSaveOptions = () => {
    if (!isPrivateOfferDefaultDirty) {
      closeOptionsDialog()
      return
    }

    setSubmittingOptions(true)
    dispatch(
      setDefaultOfferVisibilityRoutine.trigger({
        isPrivateOfferDefault: privateOfferDefaultDraft
      })
    )
  }

  useEffect(() => {
    if (!isSubmittingOptions || updateUserStatus?.state !== API_STATES.DONE) {
      return
    }

    if (updateUserStatus?.changedSuccessfully) {
      showSnackbar('success', 'common.updatedSuccessfully')
      closeOptionsDialog()
      return
    }

    dispatch(
      setSnackbarValuesRoutine({
        type: 'alert',
        title: '',
        message:
          updateUserStatus?.errorMessage ||
          intl.formatMessage({ id: 'common.errorMessage' })
      })
    )
    setSubmittingOptions(false)
  }, [dispatch, intl, isSubmittingOptions, updateUserStatus])

  const selectedRoomCreatedAt = getRoomCreatedAtLabel(selectedRoom)
  const canDeleteSelectedRoom = Boolean(canManageRooms && selectedRoom)

  const roomsLoadMoreRef = useInfiniteScrollSentinel({
    enabled: Boolean(open && roomsQuery.hasNextPage),
    hasNextPage: roomsQuery.hasNextPage,
    isFetchingNextPage: roomsQuery.isFetchingNextPage,
    fetchNextPage: roomsQuery.fetchNextPage,
    scrollContainerRef: roomsListContainerRef
  })

  const membersLoadMoreRef = useInfiniteScrollSentinel({
    enabled: Boolean(open && selectedRoomId && membersQuery.hasNextPage),
    hasNextPage: membersQuery.hasNextPage,
    isFetchingNextPage: membersQuery.isFetchingNextPage,
    fetchNextPage: membersQuery.fetchNextPage,
    scrollContainerRef: membersListContainerRef
  })

  let roomsContent = (
    <SidebarEmptyState>
      <EmptyStateTitle>
        {intl.formatMessage({ id: 'exchangeRooms.noRoomsTitle' })}
      </EmptyStateTitle>
      <EmptyStateDescription>
        {intl.formatMessage({
          id: canManageRooms
            ? 'exchangeRooms.noRoomsAdminDescription'
            : 'exchangeRooms.noRoomsReadonlyDescription'
        })}
      </EmptyStateDescription>
      {canManageRooms && (
        <PrimaryButton type='button' onClick={openCreateEditor}>
          <AddOutlinedIcon fontSize='small' />
          {intl.formatMessage({ id: 'exchangeRooms.createFirstRoom' })}
        </PrimaryButton>
      )}
    </SidebarEmptyState>
  )

  if (roomsQuery.isLoading) {
    roomsContent = (
      <StateWrapper>
        <CircularProgress size={18} />
        <span>{intl.formatMessage({ id: 'common.loading' })}</span>
      </StateWrapper>
    )
  } else if (roomsQuery.isError) {
    roomsContent = (
      <Alert
        type='alert'
        message={intl.formatMessage({ id: 'common.loadingError' })}
      />
    )
  } else if (rooms.length) {
    roomsContent = (
      <>
        <RoomsList ref={roomsListContainerRef}>
          {rooms.map(room => {
            const isSelected = room.id === selectedRoomId
            const roomCount = room.membersCount || 0

            return (
              <RoomRow
                key={room.id}
                type='button'
                selected={isSelected}
                onClick={() => setSelectedRoomId(room.id)}
              >
                <RoomMain>
                  <RoomNameRow>
                    <RoomName>
                      <RoomNameTextWrapper>
                        <EllipsisTooltip trigger={room.name}>
                          {room.name}
                        </EllipsisTooltip>
                      </RoomNameTextWrapper>
                    </RoomName>
                    {room.isDefault && (
                      <RoomBadge>
                        {intl.formatMessage({
                          id: 'forms.privateOfferRoomDefaultBadge'
                        })}
                      </RoomBadge>
                    )}
                  </RoomNameRow>
                </RoomMain>
                <RoomRowMeta>
                  {canManageRooms && (
                    <RoomStarButton
                      type='button'
                      isActive={room.isDefault}
                      onClick={e => {
                        e.stopPropagation()
                        handleToggleDefaultRoom(room.id, room.isDefault)
                      }}
                      title={getDefaultRoomActionLabel(intl, room.isDefault)}
                    >
                      <StarBorderIcon fontSize='small' />
                    </RoomStarButton>
                  )}
                  {!canManageRooms && room.isDefault && (
                    <RoomStarInfo>
                      <StarIcon fontSize='small' />
                    </RoomStarInfo>
                  )}
                  <RoomMemberCount>({roomCount})</RoomMemberCount>
                </RoomRowMeta>
              </RoomRow>
            )
          })}
          {(roomsQuery.isFetchingNextPage || roomsQuery.hasNextPage) && (
            <ListEndRef ref={roomsLoadMoreRef}>
              {roomsQuery.isFetchingNextPage && <CircularProgress size={14} />}
            </ListEndRef>
          )}
        </RoomsList>
      </>
    )
  } else if (roomSearchQuery) {
    roomsContent = (
      <InfoText>
        {intl.formatMessage({ id: 'exchangeRooms.noRoomsMatchingFilter' })}
      </InfoText>
    )
  }

  let membersContent = (
    <InfoText>{intl.formatMessage({ id: 'exchangeRooms.noMembers' })}</InfoText>
  )

  if (membersQuery.isLoading) {
    membersContent = (
      <StateWrapper>
        <CircularProgress size={18} />
        <span>{intl.formatMessage({ id: 'common.loading' })}</span>
      </StateWrapper>
    )
  } else if (membersQuery.isError) {
    membersContent = (
      <Alert
        type='alert'
        message={intl.formatMessage({ id: 'common.loadingError' })}
      />
    )
  } else if (members.length) {
    membersContent = (
      <>
        <MembersList ref={membersListContainerRef}>
          {members.map(member => {
            const companyId = getCompanyId(member)
            const action = canManageRooms ? (
              <DeleteMemberButton
                type='button'
                onClick={() => handleRemoveMember(member)}
                disabled={removeRoomMemberMutation.isLoading || !companyId}
              >
                <DeleteOutlineIcon fontSize='small' />
              </DeleteMemberButton>
            ) : null

            return (
              <CompanyListRow
                key={companyId || member.company?.taxId || member.company?.name}
                company={member.company}
                metaLabel={getCompanyDetailsLabel(member.company, intl)}
                secondaryLabel={getRoomMemberAddedAtLabel(member, intl)}
                action={action}
              />
            )
          })}
          {(membersQuery.isFetchingNextPage || membersQuery.hasNextPage) && (
            <ListEndRef ref={membersLoadMoreRef}>
              {membersQuery.isFetchingNextPage && (
                <CircularProgress size={14} />
              )}
            </ListEndRef>
          )}
        </MembersList>
      </>
    )
  }

  const membersHeaderLabel = intl.formatMessage(
    { id: 'exchangeRooms.membersSectionTitle' },
    {
      count:
        memberSearchValue.length >= MIN_SEARCH_LENGTH ||
        memberSearchValue.length === 0
          ? membersTotalCount
          : selectedRoom?.membersCount || membersTotalCount
    }
  )

  return (
    <>
      <BaseDialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth='lg'
        PaperProps={{
          style: {
            margin: 12,
            width: 'min(1120px, calc(100vw - 24px))',
            maxWidth: 'calc(100vw - 24px)',
            height: 'min(760px, calc(100vh - 24px))'
          }
        }}
        contentProps={{
          style: {
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden'
          }
        }}
      >
        <ManagerDialogContent>
          <ManagerLayout>
            <SidebarHeader>
              <SidebarHeaderTop>
                <SidebarHeaderIntro>
                  <PanelSectionTitle>
                    <span>
                      {intl.formatMessage({ id: 'exchangeRooms.roomsLabel' })}
                    </span>
                    <SidebarCount>({roomsTotalCount})</SidebarCount>
                  </PanelSectionTitle>
                  <SidebarSectionMeta>
                    {intl.formatMessage({
                      id: getExchangeLabelId(exchangeType)
                    })}
                  </SidebarSectionMeta>
                </SidebarHeaderIntro>
                {canManageRooms && <div style={{ width: 32 }} />}
              </SidebarHeaderTop>
            </SidebarHeader>

            <SidebarBody>
              <SidebarListToolbar>
                <RoomsSearchWrapper>
                  <TextInput
                    id='exchange-room-search'
                    name='roomSearch'
                    value={roomSearchValue}
                    onChange={(name, value) => setRoomSearchValue(value)}
                    placeholder={intl.formatMessage({
                      id: 'exchangeRooms.roomSearchPlaceholder'
                    })}
                    type='search'
                  />
                  {roomSearchHelper && (
                    <HelperText>{roomSearchHelper}</HelperText>
                  )}
                </RoomsSearchWrapper>
              </SidebarListToolbar>

              {roomsContent}
            </SidebarBody>

            <SidebarFooter>
              {canManageRooms && (
                <CreateRoomSidebarButton
                  type='button'
                  onClick={openCreateEditor}
                >
                  <AddOutlinedIcon fontSize='small' />
                  {intl.formatMessage({ id: 'exchangeRooms.createRoom' })}
                </CreateRoomSidebarButton>
              )}
            </SidebarFooter>

            {selectedRoom ? (
              <>
                <ContentHeader>
                  <ContentHeading>
                    <ContentHeadingBody>
                      <ContentTitleRow>
                        <ContentTitle>
                          <ContentTitleTextWrapper>
                            <EllipsisTooltip trigger={selectedRoom.name}>
                              {selectedRoom.name}
                            </EllipsisTooltip>
                          </ContentTitleTextWrapper>
                        </ContentTitle>
                        {selectedRoom.isDefault && (
                          <RoomBadge>
                            {intl.formatMessage({
                              id: 'forms.privateOfferRoomDefaultBadge'
                            })}
                          </RoomBadge>
                        )}
                        {selectedRoomCreatedAt && (
                          <ContentMetaInline>
                            {intl.formatMessage(
                              { id: 'exchangeRooms.createdAt' },
                              { date: selectedRoomCreatedAt }
                            )}
                          </ContentMetaInline>
                        )}
                      </ContentTitleRow>
                    </ContentHeadingBody>
                  </ContentHeading>

                  <HeaderToolbar>
                    {canManageRooms && (
                      <ToolbarIconButton
                        type='button'
                        title={intl.formatMessage({ id: 'common.edit' })}
                        onClick={() => openEditEditor(selectedRoom)}
                        disabled={updateRoomMutation.isLoading}
                      >
                        <EditOutlinedIcon fontSize='small' />
                      </ToolbarIconButton>
                    )}

                    {canManageRooms && (
                      <ToolbarIconButton
                        type='button'
                        title={getDefaultRoomActionLabel(
                          intl,
                          selectedRoom.isDefault
                        )}
                        onClick={() =>
                          handleToggleDefaultRoom(
                            selectedRoom.id,
                            selectedRoom.isDefault
                          )
                        }
                        disabled={updateRoomMutation.isLoading}
                      >
                        {selectedRoom.isDefault ? (
                          <StarIcon
                            fontSize='small'
                            style={{ color: '#f59e0b' }}
                          />
                        ) : (
                          <StarBorderIcon fontSize='small' />
                        )}
                      </ToolbarIconButton>
                    )}
                    {!canManageRooms && selectedRoom.isDefault && (
                      <HeaderStarInfo
                        title={intl.formatMessage({
                          id: 'forms.privateOfferRoomDefaultBadge'
                        })}
                      >
                        <StarIcon
                          fontSize='small'
                          style={{ color: '#f59e0b' }}
                        />
                      </HeaderStarInfo>
                    )}

                    <MembersSummaryBadge>
                      <GroupOutlinedIcon fontSize='small' />
                      <span>
                        {selectedRoom.membersCount || members.length || 0}
                      </span>
                    </MembersSummaryBadge>

                    {canDeleteSelectedRoom && (
                      <DeleteMemberButton
                        type='button'
                        title={intl.formatMessage({ id: 'common.remove' })}
                        onClick={() => setRoomPendingDelete(selectedRoom)}
                        disabled={deleteRoomMutation.isLoading}
                      >
                        <DeleteOutlineIcon fontSize='small' />
                      </DeleteMemberButton>
                    )}
                  </HeaderToolbar>
                </ContentHeader>

                <ContentBody ref={contentPanelRef}>
                  <MembersPanel>
                    <MembersPanelHeader>
                      <PanelSectionTitle>
                        <span>{membersHeaderLabel}</span>
                      </PanelSectionTitle>
                      <MembersHeaderRight>
                        {canManageRooms && (
                          <SectionAddButton
                            type='button'
                            onClick={() => setAddMemberDialogOpen(true)}
                            title={intl.formatMessage({
                              id: 'exchangeRooms.addMember'
                            })}
                          >
                            <AddOutlinedIcon fontSize='small' />
                          </SectionAddButton>
                        )}
                        <MembersSearchWrapper>
                          <TextInput
                            id='exchange-room-members-filter'
                            name='memberFilter'
                            value={memberFilterValue}
                            onChange={(name, value) =>
                              setMemberFilterValue(value)
                            }
                            placeholder={intl.formatMessage({
                              id: 'exchangeRooms.searchMembersPlaceholder'
                            })}
                            type='search'
                          />
                          {memberSearchHelper && (
                            <HelperText>{memberSearchHelper}</HelperText>
                          )}
                        </MembersSearchWrapper>
                      </MembersHeaderRight>
                    </MembersPanelHeader>

                    {membersContent}
                  </MembersPanel>
                </ContentBody>
              </>
            ) : (
              <ContentBody ref={contentPanelRef}>
                <ContentEmptyState>
                  <EmptyStateCard>
                    <EmptyStateTitle>
                      {intl.formatMessage({
                        id: 'exchangeRooms.selectRoomTitle'
                      })}
                    </EmptyStateTitle>
                    <EmptyStateDescription>
                      {intl.formatMessage({
                        id: 'exchangeRooms.selectRoomDescription'
                      })}
                    </EmptyStateDescription>
                  </EmptyStateCard>
                </ContentEmptyState>
              </ContentBody>
            )}

            {!isAddMemberDialogOpen && (
              <ContentFooter>
                {!canManageRooms && (
                  <FooterInfoText>
                    <InfoOutlinedIcon fontSize='small' />
                    <span>
                      {intl.formatMessage({
                        id: 'forms.privateRoomMemberReadOnlyMessage'
                      })}
                    </span>
                  </FooterInfoText>
                )}
                <SecondaryButton type='button' onClick={openOptionsDialog}>
                  <SettingsOutlinedIcon fontSize='small' />
                  {intl.formatMessage({ id: 'exchangeRooms.optionsButton' })}
                </SecondaryButton>
                <CloseModalButton type='button' onClick={onClose}>
                  {intl.formatMessage({ id: 'common.close' })}
                </CloseModalButton>
              </ContentFooter>
            )}
          </ManagerLayout>
        </ManagerDialogContent>
      </BaseDialog>

      <RoomCreateDialog
        open={isCreateRoomDialogOpen}
        onClose={closeCreateRoomDialog}
        roomName={createRoomName}
        onRoomNameChange={setCreateRoomName}
        onSave={handleSaveCreateRoom}
        isSaving={createRoomMutation.isLoading}
        intl={intl}
      />

      <RoomEditorDialog
        open={Boolean(roomEditTarget)}
        onClose={closeRoomEditDialog}
        title={intl.formatMessage({ id: 'exchangeRooms.editRoomDialogTitle' })}
        roomName={roomEditName}
        onRoomNameChange={setRoomEditName}
        roomDefault={roomEditDefault}
        onRoomDefaultChange={setRoomEditDefault}
        onSave={handleSaveRoomEdit}
        isSaving={updateRoomMutation.isLoading}
        intl={intl}
      />

      <AddMemberDialog
        open={Boolean(isAddMemberDialogOpen && selectedRoom)}
        onClose={closeAddMemberDialog}
        roomName={selectedRoom?.name}
        searchValue={candidateSearchValue}
        onSearchChange={setCandidateSearchValue}
        candidates={visibleCandidates}
        candidatesQuery={candidatesQuery}
        onAddMember={handleAddMember}
        addRoomMemberMutation={addRoomMemberMutation}
        intl={intl}
      />

      <ManagerOptionsDialog
        open={isOptionsDialogOpen}
        onClose={closeOptionsDialog}
        isPrivateOfferDefault={privateOfferDefaultDraft}
        onTogglePrivateOfferDefault={() =>
          setPrivateOfferDefaultDraft(currentValue => !currentValue)
        }
        onSave={handleSaveOptions}
        isSaving={isSavingOptions}
        isDirty={isPrivateOfferDefaultDirty}
        intl={intl}
      />

      <Dialog
        open={Boolean(roomPendingDelete)}
        onClose={() => setRoomPendingDelete(null)}
        title={intl.formatMessage({
          id: 'exchangeRooms.deleteRoomConfirmTitle'
        })}
      >
        <DialogText>
          {intl.formatMessage(
            { id: 'exchangeRooms.deleteRoomConfirmMessage' },
            {
              roomName: roomPendingDelete?.name || ''
            }
          )}
        </DialogText>
        <ActionBar>
          <CancelButton
            type='button'
            onClick={() => setRoomPendingDelete(null)}
          >
            {intl.formatMessage({ id: 'common.cancel' })}
          </CancelButton>
          <ConfirmButton
            type='button'
            onClick={() =>
              roomPendingDelete &&
              deleteRoomMutation.mutate(roomPendingDelete.id)
            }
            disabled={deleteRoomMutation.isLoading}
          >
            {intl.formatMessage({ id: 'common.remove' })}
          </ConfirmButton>
        </ActionBar>
      </Dialog>
    </>
  )
}

const ManagerDialogContent = styled.div`
  width: 100%;
  height: 100%;
  background-color: white;
  position: relative;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  min-height: 0;
`

const panelHorizontalPadding = '0.75rem'

const ManagerLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto auto auto auto auto auto;
  grid-template-areas:
    'sidebar-header'
    'sidebar-body'
    'sidebar-footer'
    'content-header'
    'content-body'
    'content-footer';
  flex: 1;
  min-height: 0;
  border-top: 1px solid ${({ theme }) => theme.colors.ghost.alpha(0.15).hex()};
  overflow: hidden;

  ${breakpoint.m`
    grid-template-columns: 320px minmax(0, 1fr);
    grid-template-rows: auto 1fr auto;
    grid-template-areas:
      'sidebar-header content-header'
      'sidebar-body content-body'
      'sidebar-footer content-footer';
  `}
`

const SidebarHeader = styled.div`
  grid-area: sidebar-header;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-self: stretch;
  min-height: 100%;
  box-sizing: border-box;
  background: #f9fafb;

  ${breakpoint.m`
    border-right: 1px solid ${({ theme }) => theme.colors.ghost.hex()};
  `}
`

const SidebarBody = styled.div`
  grid-area: sidebar-body;
  background: #f9fafb;
  display: flex;
  flex-direction: column;
  min-height: 0;
  box-sizing: border-box;
  overflow: hidden;

  ${breakpoint.m`
    border-right: 1px solid ${({ theme }) => theme.colors.ghost.hex()};
  `}
`

const ContentBody = styled.div`
  grid-area: content-body;
  background: #f3f4f6;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  min-width: 0;
  min-height: 0;
  box-sizing: border-box;
  overflow: hidden;

  ${breakpoint.m`
    overflow-y: auto;
  `}
`

const SidebarHeaderTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.5rem ${panelHorizontalPadding};
  width: 100%;
  box-sizing: border-box;
`

const SidebarHeaderIntro = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.12rem;
  min-width: 0;
  flex: 1;
`

const SidebarSectionMeta = styled.div`
  color: ${({ theme }) => theme.colors.ebonyClay.alpha(0.52).hex()};
  font-size: 0.76rem;
  font-weight: 500;
  line-height: 1.3;
  min-width: 0;
`

const SidebarListToolbar = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.4rem;
  padding: 0.4rem ${panelHorizontalPadding} 0.25rem;
  flex-shrink: 0;
`

const PanelSectionTitle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  min-height: 36px;
  min-width: 0;
  flex: 1 1 auto;
  color: ${({ theme }) => theme.colors.black.hex()};
  font-size: 0.875rem;
  font-weight: 800;
  line-height: 1.25;

  > span:first-child {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

const SidebarCount = styled.span`
  font-weight: 400;
  color: ${({ theme }) => theme.colors.ebonyClay.alpha(0.6).hex()};
  font-size: 0.8rem;
`

const listSearchFieldStyles = css`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  box-sizing: border-box;

  & > div {
    width: 100%;
  }

  & > div > span:first-child {
    display: none;
  }

  & .MuiFormControl-root,
  & .MuiTextField-root {
    width: 100%;
    margin: 0 !important;
  }
`

const panelHeaderSearchStyles = css`
  & .MuiOutlinedInput-root {
    height: 36px;
    box-sizing: border-box;
    border-radius: 4px;
  }

  & .MuiOutlinedInput-input {
    padding: 8px 0 8px 10px;
    height: 20px;
    box-sizing: border-box;
  }

  & .MuiOutlinedInput-notchedOutline {
    border: 1px solid ${({ theme }) => theme.colors.ghost.alpha(0.28).hex()};
  }

  & .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
    border-color: ${({ theme }) => theme.colors.ghost.alpha(0.5).hex()};
  }

  & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${({ theme }) => theme.colors.easternBlue.alpha(0.5).hex()};
    border-width: 1px;
    box-shadow: 0 0 0 3px
      ${({ theme }) => theme.colors.easternBlue.alpha(0.12).hex()};
  }
`

const RoomsSearchWrapper = styled.div`
  ${listSearchFieldStyles};
  ${panelHeaderSearchStyles};
  flex-shrink: 0;

  & .MuiOutlinedInput-root {
    background: #ffffff;
    border-radius: 4px;
  }

  & .MuiOutlinedInput-notchedOutline {
    border: 1px solid ${({ theme }) => theme.colors.ghost.alpha(0.28).hex()};
  }

  & .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
    border-color: ${({ theme }) => theme.colors.ghost.alpha(0.5).hex()};
  }

  & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${({ theme }) => theme.colors.easternBlue.alpha(0.5).hex()};
    border-width: 1px;
    box-shadow: 0 0 0 3px
      ${({ theme }) => theme.colors.easternBlue.alpha(0.12).hex()};
  }
`

const MembersSearchWrapper = styled.div`
  ${listSearchFieldStyles};
  ${panelHeaderSearchStyles};
  display: block;
  width: 100%;
  max-width: 100%;
  height: 36px;
  overflow: hidden;

  ${breakpoint.m`
    width: 240px;
    max-width: 240px;
  `}
`

const RoomsList = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0.25rem 0;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
`

const ListEndRef = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 2rem;
  padding: 0.25rem 0;
  color: ${({ theme }) => theme.colors.ebonyClay.alpha(0.6).hex()};
`

const RoomRow = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.5rem ${panelHorizontalPadding};
  text-align: left;
  border: none;
  border-left: 3px solid
    ${({ selected, theme }) =>
      selected ? theme.colors.apple.hex() : 'transparent'};
  border-bottom: 1px solid ${({ theme }) => theme.colors.ghost.alpha(0.3).hex()};
  border-radius: 0;
  background: ${({ selected, theme }) =>
    selected ? theme.colors.apple.alpha(0.06).hex() : 'transparent'};
  cursor: pointer;
  transition: border-color 0.2s, background-color 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.apple.alpha(0.03).hex()};
  }
`

const RoomMain = styled.div`
  min-width: 0;
  flex: 1;
`

const RoomRowMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-shrink: 0;
  margin-left: 0.5rem;
`

const RoomNameRow = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 0.4rem;
  flex-wrap: nowrap;
`

const RoomName = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.25rem;
  min-width: 0;
  color: ${({ theme }) => theme.colors.ebonyClay.hex()};
  font-size: 0.98rem;
  font-weight: 700;
`

const RoomNameTextWrapper = styled.div`
  flex: 1;
  min-width: 0;
`

const RoomMemberCount = styled.span`
  color: ${({ theme }) => theme.colors.ebonyClay.alpha(0.6).hex()};
  font-weight: 400;
`

const RoomBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 20px;
  padding: 0.1rem 0.45rem;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.apple.alpha(0.12).hex()};
  color: ${({ theme }) => theme.colors.apple.hex()};
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
`

const ContentHeader = styled.div`
  grid-area: content-header;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  align-self: stretch;
  min-height: 100%;
  box-sizing: border-box;
  padding: 0.5rem ${panelHorizontalPadding};
  background: #ffffff;
  border-bottom: 1px solid
    ${({ theme }) => theme.colors.ghost.alpha(0.22).hex()};

  ${breakpoint.m`
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
  `}
`

const ContentHeading = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  min-width: 0;
  flex: 1;
`

const ContentHeadingBody = styled.div`
  min-width: 0;
  flex: 1;
`

const ContentTitleRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  min-width: 0;
  gap: 0.4rem 0.5rem;
`

const ContentTitle = styled.h4`
  margin: 0;
  min-width: 0;
  color: ${({ theme }) => theme.colors.ebonyClay.hex()};
  font-size: 1.05rem;
  line-height: 1.25;
  font-weight: 800;
`

const ContentTitleTextWrapper = styled.div`
  display: block;
  min-width: 0;
`

const ContentMetaInline = styled.span`
  color: ${({ theme }) => theme.colors.ebonyClay.alpha(0.65).hex()};
  font-size: 0.78rem;
  line-height: 1.25;
  white-space: nowrap;
  flex-shrink: 0;
`

const HeaderToolbar = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: flex-start;
  gap: 0.6rem;
  flex-wrap: wrap;
  flex-shrink: 0;

  ${breakpoint.m`
    width: auto;
    justify-content: flex-end;
  `}
`

const MembersSummaryBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  height: 38px;
  padding: 0 0.7rem;
  border-radius: 4px;
  background: transparent;
  color: ${({ theme }) => theme.colors.ebonyClay.hex()};
  font-size: 0.8rem;
  font-weight: 600;
  border: 1px solid ${({ theme }) => theme.colors.ghost.alpha(0.3).hex()};
`

const ToolbarIconButton = styled.button`
  width: 38px;
  height: 38px;
  border: 1px solid ${({ theme }) => theme.colors.ghost.alpha(0.35).hex()};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.white.hex()};
  color: ${({ theme }) => theme.colors.ebonyClay.hex()};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.apple.alpha(0.5).hex()};
    color: ${({ theme }) => theme.colors.apple.hex()};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const HeaderStarInfo = styled.span`
  width: 38px;
  height: 38px;
  border: 1px solid ${({ theme }) => theme.colors.ghost.alpha(0.35).hex()};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.white.hex()};
  color: ${({ theme }) => theme.colors.ebonyClay.hex()};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: default;
`

const SectionAddButton = styled.button`
  width: 36px;
  height: 36px;
  min-width: 36px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.easternBlue.hex()};
  color: ${({ theme }) => theme.colors.white.hex()};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;

  svg {
    display: block;
  }

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.easternBlue.darken(0.08).hex()};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`

const MembersPanel = styled.div`
  padding: 1rem ${panelHorizontalPadding} 0.5rem;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;

  ${breakpoint.m`
    padding: 1rem ${panelHorizontalPadding} 1rem;
  `}
`

const MembersPanelHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  flex-shrink: 0;
  flex-wrap: wrap;

  ${breakpoint.m`
    flex-wrap: nowrap;
  `}
`

const MembersList = styled.div`
  min-height: 0;
  overflow-y: auto;
`

const CompanyRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: ${({ compact }) => (compact ? '0.7rem 0' : '1rem 1.25rem')};
  background: ${({ compact }) => (compact ? 'transparent' : '#FFFFFF')};
  border-bottom: ${({ compact, theme }) =>
    compact ? `1px solid ${theme.colors.ghost.alpha(0.3).hex()}` : 'none'};
  border-radius: ${({ compact }) => (compact ? '0' : '4px')};
  border: ${({ compact }) => (compact ? 'none' : '1px solid #E5E7EB')};
  margin-bottom: ${({ compact }) => (compact ? '0' : '0.75rem')};
  box-shadow: ${({ compact }) =>
    compact ? 'none' : '0 1px 2px rgba(0,0,0,0.05)'};
  transition: ${({ compact }) =>
    compact ? 'none' : 'box-shadow 0.2s ease-out, border-color 0.2s ease-out'};

  &:hover {
    ${({ compact }) =>
      !compact &&
      css`
        border-color: #d1d5db;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
      `}
  }
`

const AvatarPlaceholder = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.wildSand.hex()};
  color: ${({ theme }) => theme.colors.ghost.darken(0.15).hex()};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const CompanyInfo = styled.div`
  min-width: 0;
  flex: 1;
`

const CompanyName = styled.div`
  color: ${({ theme }) => theme.colors.ebonyClay.hex()};
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.35;
`

const CompanyMeta = styled.div`
  margin-top: 0.12rem;
  color: ${({ theme }) => theme.colors.ebonyClay.alpha(0.65).hex()};
  font-size: 0.84rem;
  line-height: 1.45;
  white-space: pre-line;
`

const CompanyMetaSubtle = styled.div`
  margin-top: 0.12rem;
  color: ${({ theme }) => theme.colors.ebonyClay.alpha(0.56).hex()};
  font-size: 0.78rem;
  line-height: 1.4;
`

const DeleteMemberButton = styled.button`
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.fadedRed.alpha(0.1).hex()};
  color: ${({ theme }) => theme.colors.fadedRed.hex()};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`

const CandidateList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 0.75rem 0.5rem;
`

const AddMemberInlineButton = styled.button`
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.apple.alpha(0.1).hex()};
  color: ${({ theme }) => theme.colors.apple.hex()};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`

const AddMemberSearchField = styled.div`
  ${listSearchFieldStyles};
  flex-shrink: 0;
`

const AddMemberSearchHint = styled.div`
  margin-top: 0.35rem;
  color: ${({ theme }) => theme.colors.ebonyClay.alpha(0.56).hex()};
  font-size: 0.78rem;
  line-height: 1.4;
  flex-shrink: 0;
`

const AddMemberResultsArea = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 160px;
  max-height: 280px;
  margin-top: 0.75rem;
  border: 1px solid ${({ theme }) => theme.colors.ghost.alpha(0.2).hex()};
  border-radius: 8px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.white.hex()};
`

const AddMemberResultsState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 120px;
  padding: 1rem 0.75rem;
  text-align: center;
`

const AddMemberResultsPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  margin-top: 0.75rem;
  padding: 1rem 0.75rem;
  border: 1px dashed ${({ theme }) => theme.colors.ghost.alpha(0.35).hex()};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.wildSand.alpha(0.35).hex()};
  text-align: center;
`

const CompactDialogContent = styled.div`
  width: 100%;
  background-color: white;
  position: relative;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  min-height: 0;
  max-height: min(480px, calc(100vh - 32px));
  padding: 1.25rem 1.25rem 1rem;
`

const CompactDialogHeader = styled.div`
  flex-shrink: 0;
  padding-right: 2rem;
  margin-bottom: 0.85rem;
`

const CompactDialogTitle = styled.h3`
  margin: 0;
  color: ${({ theme }) => theme.colors.ebonyClay.hex()};
  font-size: 1.08rem;
  line-height: 1.3;
  font-weight: 800;
`

const CompactDialogDescription = styled.p`
  margin: 0.35rem 0 0;
  color: ${({ theme }) => theme.colors.ebonyClay.alpha(0.72).hex()};
  font-size: 0.88rem;
  line-height: 1.5;
`

const CompactModalCloseIcon = styled(ModalCloseIcon)`
  top: 14px;
  right: 12px;
  width: 26px;
  height: 26px;
`

const OptionsCard = styled.div`
  padding: 0.25rem 0 0.5rem;
`

const SidebarFooter = styled.div`
  grid-area: sidebar-footer;
  display: flex;
  align-items: center;
  padding: 0.75rem;
  background: #f9fafb;
  flex-shrink: 0;

  ${breakpoint.m`
    border-right: 1px solid #E5E7EB;
  `}
`

const CreateRoomSidebarButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: ${({ theme }) => theme.colors.easternBlue.hex()};
  color: ${({ theme }) => theme.colors.white.hex()};
  border: none;
  border-radius: 4px;
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease-out, transform 0.1s ease-out;

  &:hover {
    background: ${({ theme }) => theme.colors.easternBlue.darken(0.08).hex()};
  }

  &:active {
    transform: scale(0.98);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`

const RoomStarButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  opacity: ${({ isActive }) => (isActive ? 1 : 0)};
  transition: all 0.15s ease-out;
  flex-shrink: 0;

  ${RoomRow}:hover & {
    opacity: 1;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.wildSand.hex()};
    color: ${({ theme }) => theme.colors.easternBlue.hex()};
  }

  ${({ isActive }) =>
    isActive &&
    css`
      color: #f59e0b;
      &:hover {
        color: #dc2626;
      }
    `}
`

const RoomStarInfo = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  color: #f59e0b;
`

const MembersHeaderRight = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  flex-shrink: 0;
`

const ContentFooter = styled.div`
  grid-area: content-footer;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.75rem;
  padding: 0.7rem ${panelHorizontalPadding} 0.9rem;
  border-top: 1px solid #e5e7eb;
  background: #ffffff;
  flex-shrink: 0;
`

const FooterInfoText = styled.span`
  margin-right: auto;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  color: ${({ theme }) => theme.colors.ebonyClay.alpha(0.65).hex()};
  font-size: 0.75rem;
  line-height: 1.4;

  svg {
    color: ${({ theme }) => theme.colors.ebonyClay.alpha(0.45).hex()};
    flex-shrink: 0;
  }
`

const EditorActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 0.5rem;
`

const BaseActionButton = styled.button`
  min-height: 40px;
  padding: 0.55rem 0.95rem;
  border-radius: 4px;
  border: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  font-size: 0.84rem;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`

const PrimaryButton = styled(BaseActionButton)`
  background: ${({ theme }) => theme.colors.easternBlue.hex()};
  color: ${({ theme }) => theme.colors.white.hex()};
  max-width: 100%;
`

const SecondaryButton = styled(BaseActionButton)`
  background: ${({ theme }) => theme.colors.wildSand.hex()};
  color: ${({ theme }) => theme.colors.ebonyClay.hex()};
`

const CloseModalButton = styled(BaseActionButton)`
  background: #ffffff;
  color: ${({ theme }) => theme.colors.ebonyClay.hex()};
  border: 1px solid ${({ theme }) => theme.colors.ghost.alpha(0.4).hex()};
  min-width: 112px;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.wildSand.hex()};
    border-color: ${({ theme }) => theme.colors.ghost.alpha(0.6).hex()};
  }
`

const SaveButton = styled(BaseActionButton)`
  background: ${({ theme }) => theme.colors.apple.hex()};
  color: ${({ theme }) => theme.colors.white.hex()};
`

const EmptyStateCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 0.7rem;
  min-height: 220px;
  padding: 0.5rem 0;
`

const SidebarEmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 0.7rem;
  padding: 0.5rem 0.25rem;
  text-align: center;
`

const ContentEmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 0;
  padding: 1.5rem;
`

const EmptyStateTitle = styled.div`
  color: ${({ theme }) => theme.colors.ebonyClay.hex()};
  font-size: 1rem;
  font-weight: 700;
`

const EmptyStateDescription = styled.div`
  color: ${({ theme }) => theme.colors.ebonyClay.alpha(0.72).hex()};
  font-size: 0.88rem;
  line-height: 1.6;
`

const StateWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: ${({ theme }) => theme.colors.ebonyClay.alpha(0.72).hex()};
  font-size: 0.88rem;
`

const InfoText = styled.div`
  color: ${({ theme }) => theme.colors.ebonyClay.alpha(0.72).hex()};
  font-size: 0.88rem;
  line-height: 1.6;
`

const HelperText = styled.div`
  margin-top: 0.2rem;
  margin-bottom: 0;
  color: ${({ theme }) => theme.colors.fadedRed.hex()};
  font-size: 0.78rem;
  text-align: center;
`

export default PrivateRoomsManager
